import { useEffect, useState } from "react";
import Div from "../../../../../components/base/Div";
import Icon from "../../../../../components/base/Icon";
import Indicator from "../../../../../components/base/Indicator";
import Modal from "../../../../../components/base/Modal";
import { useHub } from "../../../../../core/Global";
import FlexRow from "../../../../../components/base/FlexRow";
import { copyToClipboard } from "../../../../../core/Helper";


type Props = {
    title: string
    // Base endpoint that supports GET (read current token) and POST (generate a new one),
    // e.g. "/accounts/profile/restapitoken/" or "/accounts/profile/mcptoken/".
    apiUrl: string
    // Unique id for the regenerate-confirmation modal (must differ per card on the page).
    modalId: string
    emptyText?: string
    // Optional one-time notice (e.g. terms) shown ONLY the first time a token is generated
    // (i.e. when none exists yet). The user must accept it before the first token is created.
    firstTimeNotice?: any
    firstTimeNoticeModalId?: string
    firstTimeNoticeTitle?: string
    firstTimeNoticeAcceptText?: string
}


export default function ApiTokenCard(props: Props) {

    const hub = useHub()
    const [fetching, fetchingSet] = useState<boolean>(true)
    const [fetched, fetchedSet] = useState<boolean>(false)

    const [token, tokenSet] = useState<string>()

    const [working, workingSet] = useState<boolean>(false)

    const fetch = () => {
        hub.Get(props.apiUrl, res => {
            tokenSet(res.data)
            fetchingSet(false)
            fetchedSet(true)
        })
    }

    useEffect(fetch, [])

    const generate = (callback?: any) => {
        workingSet(true)
        hub.Post(props.apiUrl, {}, res => {
            tokenSet(res.data)
            workingSet(false)
            if (callback) callback()
        })
    }

    const onGenerateClick = () => {
        if (!window.isNullOrEmpty(token)) {
            // A token already exists -> regenerating invalidates it, so confirm first.
            window.openModal(props.modalId)
        }
        else if (props.firstTimeNotice && props.firstTimeNoticeModalId) {
            // First-time generation -> require the user to accept the notice first.
            window.openModal(props.firstTimeNoticeModalId)
        }
        else generate()
    }

    return (
        <div className="card mb-5">
            <div className="card-header border-0">
                <div className="card-title m-0">
                    <h3 className="fw-bolder m-0">{props.title}</h3>
                </div>
                <div className="card-action">
                    <button type="button" className="btn btn-sm btn-light-primary" disabled={working}
                        onClick={e => {
                            e.preventDefault()
                            onGenerateClick()
                        }}>
                        {
                            working ? <Indicator text="Generate Token..." />
                                :
                                <>
                                    <Icon icon="refresh" className="me-2" />
                                    Generate Token
                                </>
                        }
                    </button>
                </div>
            </div>
            <div className="card-body border-top p-9">

                <Div visible={fetching} className="my-20">
                    <Indicator />
                </Div>

                <Div visible={fetched && window.isNullOrEmpty(token)}>
                    <div className="">
                        {props.emptyText ?? `You don't have a ${props.title}...`}
                    </div>
                </Div>

                <Div visible={fetched && !window.isNullOrEmpty(token)}>
                    <FlexRow className="alert alert-primary" wrap>
                        <input value={token} type="text" readOnly className="form-control form-control-sm me-2"
                            style={{
                                maxWidth: "80%"
                            }} />
                        <a href="#"
                            onClick={async e => {
                                e.preventDefault()
                                await copyToClipboard(token, "Token copied to clipboard...")
                            }}>
                            <Icon icon="copy" size={14} />
                        </a>
                    </FlexRow>
                </Div>

            </div>

            <Modal id={props.modalId}
                title={"Regenerate " + props.title}
                buttonText="Regenerate Token"
                buttonColor="danger"
                buttonIcon="refresh"
                onConfirm={callback => generate(callback)}>
                <div className="mb-3">
                    Generating a new token will <b>permanently invalidate</b> the current {props.title}.
                    Any integration still using the old token will stop working until it is updated
                    with the new one.
                    <div className="mt-3">Are you sure you want to continue?</div>
                </div>
            </Modal>

            {
                props.firstTimeNotice && props.firstTimeNoticeModalId &&
                <Modal id={props.firstTimeNoticeModalId}
                    title={props.firstTimeNoticeTitle ?? "Notice"}
                    size="Large"
                    staticBackdrop
                    buttonText={props.firstTimeNoticeAcceptText ?? "Accept & Generate Token"}
                    buttonColor="primary"
                    buttonIcon="check"
                    onConfirm={callback => generate(callback)}>
                    {props.firstTimeNotice}
                </Modal>
            }

        </div>
    )
}
